import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { GenericRequest } from '@app/core/models/request-status.model';
import { ProductListService } from '@b2b/features/product/core/services/product-list.service';
import { FACET_TYPE, Facet, Facets, ValuesFacets } from '@b2b/features/product/core/utilities/product-list.model';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';

@Component({
  selector: 'facets-to-apply',
  imports: [CommonModule, TranslateModule],
  templateUrl: './facets-to-apply.component.html',
  styleUrl: './facets-to-apply.component.scss',
})
export class FacetsToApplyComponent implements OnInit {
  @Input() facetGroups!: GenericRequest<Facets[]>;
  @ViewChild('facetsContainer') facetsContainer!: ElementRef;
  FACET_TYPE = FACET_TYPE;
  arrayFacets: Facet[] = [];
  isExpandedMap: { [key: string]: boolean } = {};

  constructor(
    private productListService: ProductListService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  getIt(group: Facets, facet: ValuesFacets) {
    this.productListService
      .get()
      .pipe(take(1))
      .subscribe(productList => {
        const currentQuery = this.activatedRoute.snapshot.queryParams['query'] || '*:score';

        this.addQueryParamsOnUrl({
          ['query']: `${currentQuery}:${group.code}:${facet.name}`,
          ['page']: 1,
        });

        const appliedFacets = productList.appliedFacets || [];
        const finnedGroup = appliedFacets.find(applied => applied.group.code === group.code);

        if (!finnedGroup) {
          return this.productListService.setFacetsApplied([...appliedFacets, { group, facets: [facet] }]);
        }

        const editedFacets = appliedFacets.reduce((acc, applied) => {
          if (applied.group.code === group.code) {
            if (group.type === FACET_TYPE.RADIO) {
              acc.push({
                ...applied,
                facets: [facet],
              });
              return acc;
            }

            if (applied.facets.some(appliedFacet => appliedFacet.name === facet.name)) {
              const filteredFacetsOnGroup = applied.facets.filter(group => group.name !== facet.name);

              const regex = new RegExp(`:\\w+:${facet.name}`, 'g');
              const param = currentQuery.replace(regex, '');

              this.addQueryParamsOnUrl({
                ['query']: param,
                ['page']: 1,
              });

              acc.push({
                ...applied,
                facets: filteredFacetsOnGroup,
              });

              return acc;
            }
          }

          acc.push(applied);
          return acc;
        }, [] as Facet[]);

        return this.productListService.setFacetsApplied(editedFacets.filter(applied => applied.facets.length > 0));
      });
  }

  ngOnInit(): void {
    this.checkFacetsUrl();
  }

  checkFacetsUrl() {
    const currentQuery = this.activatedRoute.snapshot.queryParams['query'];

    if (currentQuery) {
      this.facetGroups.data?.map(group => {
        const foundItems = group.values?.map(
          item => currentQuery.includes(item.name) && currentQuery.includes(group.code) && item,
        );

        if (foundItems) {
          foundItems
            .filter(value => typeof value != 'boolean' && value != null)
            .map(value => {
              this.arrayFacets.push({ group: group, facets: [value] });
              this.productListService.setFacetsApplied([...this.arrayFacets]);
              this.isChecked(value);
            });
        }
      });
    }
  }

  isChecked(facet: ValuesFacets) {
    let isChecked = false;

    this.productListService
      .get()
      .pipe(take(1))
      .subscribe(productList => {
        isChecked =
          productList.appliedFacets?.some(applied =>
            applied.facets.some(facetOnGroup => facetOnGroup.name === facet.name),
          ) || false;
      });

    return isChecked;
  }

  addQueryParamsOnUrl(queryParams: Params | null) {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  toggleExpand(groupCode: string) {
    this.isExpandedMap[groupCode] = !this.isExpandedMap[groupCode];
  }

  isGroupExpanded(groupCode: string): boolean {
    return !!this.isExpandedMap[groupCode];
  }
}
